import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/themes/light-border.css';
import DraggableYearlyMultiLineChart, {
  generateDefaultCurves,
} from '../../../components/utility_planning/DraggableYearlyMultiLineChart';
import ZctaOverlapAdoptionMap from '../../../components/utility_planning/ZctaOverlapAdoptionMap';
import { getSelectedUtility } from '../../../utils/utilitySelection';
import {
  downloadAdoptionCurveTemplate,
  parseAdoptionCurveXlsx,
} from '../../../utils/adoptionCurveXlsx';
import { getUtilityWorkflowUiState, updateUtilityWorkflowUiState } from '../../../utils/utilityWorkflowUiState';

const DEFAULT_START_YEAR = 2026;
const DEFAULT_END_YEAR = 2050;
const DEFAULT_PASSENGER_CAR_POPULATION = 1000000;

function PassengerEvAdoptionTrendPage() {
  const storedUiState = getUtilityWorkflowUiState().passengerAdoptionTrend;
    const [startYear, setStartYear] = useState(storedUiState?.startYear ?? DEFAULT_START_YEAR);
  const [endYear, setEndYear] = useState(storedUiState?.endYear ?? DEFAULT_END_YEAR);
  const [passengerCarPopulation, setPassengerCarPopulation] = useState(
    storedUiState?.passengerCarPopulation ?? DEFAULT_PASSENGER_CAR_POPULATION
  );
  const [curves, setCurves] = useState(() =>
    generateDefaultCurves(DEFAULT_START_YEAR, DEFAULT_END_YEAR)
  );
  const [curveInputMode, setCurveInputMode] = useState(storedUiState?.curveInputMode || 'builtin');
  const [uploadError, setUploadError] = useState(null);
  const xlsxInputRef = useRef(null);

  const startYearNum = Number(startYear) || DEFAULT_START_YEAR;
  const endYearNum = Number(endYear) || DEFAULT_END_YEAR;
  const validRange = startYearNum < endYearNum;

  const selection = getSelectedUtility();

  useEffect(() => {
    if (!validRange) return;
    setCurves(generateDefaultCurves(startYearNum, endYearNum));
  }, [startYearNum, endYearNum, validRange]);

  useEffect(() => {
    updateUtilityWorkflowUiState('passengerAdoptionTrend', {
      startYear,
      endYear,
      passengerCarPopulation,
      curveInputMode,
    });
  }, [curveInputMode, endYear, passengerCarPopulation, startYear]);

  const handleCurvesUpdate = useCallback((newCurves) => {
    setCurves(newCurves);
  }, []);

  const handleUpdateAdoptionCurves = () => {
    if (!validRange) return;
    setUploadError(null);
    setCurves(generateDefaultCurves(startYearNum, endYearNum));
  };

  const handleDownloadTemplate = useCallback(async () => {
    setUploadError(null);
    try {
      await downloadAdoptionCurveTemplate({
        startYear: startYearNum,
        endYear: endYearNum,
        baselinePoints: curves.baseline || [],
        utilityName: selection?.utilityName,
      });
    } catch (err) {
      setUploadError(err.message || 'Failed to download template.');
    }
  }, [startYearNum, endYearNum, curves.baseline, selection?.utilityName]);

  const handleUploadXlsx = useCallback(
    async (e) => {
      const file = e.target.files?.[0];
      e.target.value = '';
      setUploadError(null);
      if (!file) return;
      try {
        const { points } = await parseAdoptionCurveXlsx(file, startYearNum, endYearNum);
        setCurves({ baseline: points });
      } catch (err) {
        setUploadError(err.message || 'Invalid file. Please use the downloaded template.');
      }
    },
    [startYearNum, endYearNum]
  );

  return (
    <div className="min-h-screen bg-gray-50 font-ui">
        <div className="p-8 max-w-6xl mx-auto">
          <div className="mb-6">
            <Link
              to="/utility/workflow"
              className="text-blue-600 hover:text-blue-800 font-medium text-sm"
            >
              ← Back to Workflow
            </Link>
          </div>

          <h1 className="text-3xl font-bold text-gray-800 mb-2">EV Adoption Trend</h1>
          <p className="text-gray-600 mb-2">
            Configure forecast settings for {selection.utilityName} ({selection.stateCode}).
          </p>
          {!selection?.stateCode && !selection?.utilityName && (
            <section className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
              <p className="text-gray-800 font-ui mb-3">
                Select a state and utility to configure adoption forecasting.
              </p>
              <Link
                to="/utility/select"
                className="inline-flex px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
              >
                Select state and utility
              </Link>
            </section>
          )}

          {/* Forecast range + Passenger Car population */}
          <section className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <div className="flex-1 min-w-0">
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Forecast settings</h2>
                <div className="flex flex-col sm:flex-row gap-4">
                  <label className="flex flex-col gap-1 flex-1 max-w-xs">
                    <span className="text-sm font-medium text-gray-700">Start year</span>
                    <input
                      type="number"
                      min={2000}
                      max={2100}
                      value={startYear}
                      onChange={(e) => setStartYear(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50"
                    />
                  </label>
                  <label className="flex flex-col gap-1 flex-1 max-w-xs">
                    <span className="text-sm font-medium text-gray-700">End year</span>
                    <input
                      type="number"
                      min={2000}
                      max={2100}
                      value={endYear}
                      onChange={(e) => setEndYear(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50"
                    />
                  </label>
                </div>
              </div>
              <div className="flex-1 min-w-0 md:max-w-sm mt-4 md:mt-0">
                <label className="flex flex-col gap-1 w-full">
                  <span className="text-sm font-medium text-gray-700">Passenger Car population</span>
                  <input
                    type="number"
                    min={0}
                    step={10000}
                    value={passengerCarPopulation}
                    onChange={(e) => setPassengerCarPopulation(Number(e.target.value) || 0)}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-gray-50"
                  />
                </label>
              </div>
            </div>
          </section>

          {/* Utility territory adoption forecasting */}
          <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Utility territory adoption forecasting
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              Bass diffusion–style adoption curves. Drag points to adjust EV adoption % by year.
            </p>
            {validRange ? (
              <DraggableYearlyMultiLineChart
                startYear={startYearNum}
                endYear={endYearNum}
                curves={curves}
                onCurvesUpdate={handleCurvesUpdate}
                xLabel="Year"
                yLabel="EV adoption (%)"
                height={360}
              />
            ) : (
              <p className="text-amber-600 py-4">Set start year &lt; end year to show the chart.</p>
            )}

            <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <div className="flex items-start justify-between gap-2">
                  <label className="inline-flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="curve-input-mode"
                      checked={curveInputMode === 'builtin'}
                      onChange={() => setCurveInputMode('builtin')}
                      className="mt-1 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-800 font-ui">
                      Option 1 - Use Build-in Forecasting
                    </span>
                  </label>
                  <Tippy
                    interactive
                    trigger="click"
                    content={
                      <div className="max-w-xs text-sm font-ui p-1">
                        <p className="text-gray-700">
                          This option generates a built-in baseline adoption curve from the selected forecast
                          years and system defaults.
                        </p>
                      </div>
                    }
                    placement="top"
                    theme="light-border"
                  >
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-[11px] leading-none font-bold hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 shrink-0"
                      aria-label="How built-in adoption curve is generated"
                    >
                      ?
                    </button>
                  </Tippy>
                </div>
                <div className="mt-3">
                  <div className="inline-flex items-start gap-1">
                    <button
                      type="button"
                      onClick={handleUpdateAdoptionCurves}
                      disabled={curveInputMode !== 'builtin' || !validRange}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors font-ui"
                    >
                      Update Adoption Curve
                    </button>
                  </div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <div className="flex items-start justify-between gap-2">
                  <label className="inline-flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="curve-input-mode"
                      checked={curveInputMode === 'custom'}
                      onChange={() => setCurveInputMode('custom')}
                      className="mt-1 text-blue-600"
                    />
                    <span className="text-sm font-medium text-gray-800 font-ui">
                      Option 2 - Use Custom Forecasting
                    </span>
                  </label>
                  <Tippy
                    interactive
                    trigger="click"
                    placement="top"
                    theme="light-border"
                    content={
                      <div className="max-w-xs text-sm font-ui p-1">
                        <p className="text-gray-700 mb-2">
                          Download the template first, customize the adoption curve values, then upload
                          your filled file.
                        </p>
                        <button
                          type="button"
                          onClick={handleDownloadTemplate}
                          className="px-3 py-1.5 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
                        >
                          Download template
                        </button>
                      </div>
                    }
                  >
                    <button
                      type="button"
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 text-[11px] leading-none font-bold hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 shrink-0"
                      aria-label="How to use custom forecasting template"
                    >
                      ?
                    </button>
                  </Tippy>
                </div>
                <div className="mt-3">
                  <div className="inline-flex items-start gap-1">
                    <button
                      type="button"
                      onClick={() => xlsxInputRef.current?.click()}
                      disabled={curveInputMode !== 'custom'}
                      className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-ui"
                    >
                      Upload Curve
                    </button>
                  </div>
                </div>
                <input
                  ref={xlsxInputRef}
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleUploadXlsx}
                  className="hidden"
                  aria-label="Upload adoption curve XLSX"
                />
              </div>
            </div>
            {uploadError && (
              <p className="mt-3 text-sm text-red-600 font-ui">{uploadError}</p>
            )}
          </section>

          <section className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">Check zip code level adoption</h2>
            <p className="text-sm text-gray-600 mb-4">
              View ZCTA areas that overlap with the selected utility territory. Click a ZCTA to
              open its adoption forecast curve.
            </p>
            {selection?.stateCode && selection?.utilityName ? (
              <ZctaOverlapAdoptionMap
                selection={selection}
                height={420}
                startYear={startYearNum}
                endYear={endYearNum}
              />
            ) : (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                <p className="text-sm text-gray-800 mb-3 font-ui">
                  Select a state and utility before checking ZIP-level adoption.
                </p>
                <Link
                  to="/utility/select"
                  className="inline-flex px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
                >
                  Select state and utility
                </Link>
              </div>
            )}
          </section>
        </div>
    </div>
  );
}

export default PassengerEvAdoptionTrendPage;
